import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import { getCountryOptions, getCitiesByCountry } from '../utils/countryCityData';
import NationalitySelect from './NationalitySelect';
import './StudentForm.css';

const StudentForm = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const [deletedStudent, setDeletedStudent] = useState(null);
  const [checkingDNI, setCheckingDNI] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      birth_date: '',
      gender: '',
      document_type: '',
      document_number: '',
      email: '',
      phone: '',
      parent_name: '',
      parent_phone: '',
      parent_email: '',
      parent_relationship: '',
      address: '',
      country: 'Perú',
      city: '',
      medical_notes: '',
      uniform_size: '',
      ...(initialData || {}),
      nationality_code: initialData?.nationality_code || 'PE',
    },
  });

  const selectedCountry = watch('country');
  const documentNumber = watch('document_number');

  // Observar la fecha de nacimiento para validar si es menor de 18 años
  const birthDate = watch('birth_date');
  
  // Verificar DNI cuando se ingresa (solo si no estamos editando)
  const handleDNIBlur = async () => {
    if (!documentNumber || initialData || checkingDNI) return;
    
    setCheckingDNI(true);
    try {
      const response = await api.get(`/students/check-dni/${documentNumber}`);
      
      if (response.data.exists && response.data.is_deleted) {
        setDeletedStudent(response.data.student);
        toast.warning(`Este DNI pertenece a un alumno eliminado: ${response.data.student.full_name}. Puedes restaurarlo.`, {
          autoClose: 8000
        });
      } else if (response.data.exists && !response.data.is_deleted) {
        toast.error('Este DNI ya está registrado en un alumno activo');
      }
    } catch (error) {
      console.error('Error al verificar DNI:', error);
    } finally {
      setCheckingDNI(false);
    }
  };
  
  // Mostrar confirmación antes de restaurar
  const handleRestoreClick = () => {
    setConfirmRestore(true);
  };
  
  // Restaurar estudiante eliminado
  const handleRestoreStudent = async () => {
    if (!deletedStudent) return;
    
    try {
      const response = await api.post(`/students/${deletedStudent.id}/restore`);
      toast.success(`Alumno "${response.data.student_name}" restaurado exitosamente`);
      setDeletedStudent(null);
      setConfirmRestore(false);
      if (onCancel) onCancel(); // Cerrar el formulario
      window.location.reload(); // Recargar para ver el alumno restaurado
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar alumno');
      setConfirmRestore(false);
    }
  };
  
  // Función para calcular si es menor de 18 años
  const isMinor = () => {
    if (!birthDate) return false;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age < 18;
  };

  return (
    <>
      <ConfirmDialog
        isOpen={confirmRestore}
        onClose={() => setConfirmRestore(false)}
        onConfirm={handleRestoreStudent}
        title={`¿Restaurar a ${deletedStudent?.full_name}?`}
        message="El alumno volverá a estar activo con toda su información y historial conservados."
        type="success"
        confirmText="Restaurar"
        cancelText="Cancelar"
      />
      <form onSubmit={handleSubmit(onSubmit)} className="student-form">
      <div className="form-section">
        <h4>Datos Personales</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">Nombre *</label>
            <input
              type="text"
              id="first_name"
              {...register('first_name', { required: 'El nombre es obligatorio' })}
              className={errors.first_name ? 'input-error' : ''}
            />
            {errors.first_name && <span className="error-message">{errors.first_name.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="last_name">Apellidos *</label>
            <input
              type="text"
              id="last_name"
              {...register('last_name', { required: 'Los apellidos son obligatorios' })}
              className={errors.last_name ? 'input-error' : ''}
            />
            {errors.last_name && <span className="error-message">{errors.last_name.message}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="birth_date">Fecha de Nacimiento *</label>
            <input
              type="date"
              id="birth_date"
              {...register('birth_date', { required: 'La fecha de nacimiento es obligatoria' })}
              className={errors.birth_date ? 'input-error' : ''}
            />
            {errors.birth_date && <span className="error-message">{errors.birth_date.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="gender">Género</label>
            <select id="gender" {...register('gender')}>
              <option value="">Seleccionar...</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="document_type">Tipo de Documento</label>
            <select id="document_type" {...register('document_type')}>
              <option value="">Seleccionar...</option>
              <option value="DNI">DNI</option>
              <option value="NIE">Carnet de extranjería</option>
              <option value="Pasaporte">Pasaporte</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="document_number">Número de Documento</label>
            <input
              type="text"
              id="document_number"
              {...register('document_number')}
              onChange={(e) => {
                // Limpiar alerta de estudiante eliminado al modificar el DNI
                if (deletedStudent) {
                  setDeletedStudent(null);
                }
                // Llamar al onChange del register
                register('document_number').onChange(e);
              }}
              onBlur={handleDNIBlur}
              disabled={checkingDNI}
            />
            {checkingDNI && <small style={{ color: '#666' }}>Verificando DNI...</small>}
            {deletedStudent && (
              <div style={{ 
                marginTop: '8px', 
                padding: '10px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffc107',
                borderRadius: '4px'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#856404' }}>
                  ⚠️ Este DNI pertenece a <strong>{deletedStudent.full_name}</strong> (eliminado)
                </p>
                <button
                  type="button"
                  onClick={handleRestoreClick}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  🔄 Restaurar Alumno
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Contacto del Alumno</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email (Opcional)</label>
            <input
              type="email"
              id="email"
              {...register('email')}
              placeholder="email@ejemplo.com"
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Teléfono del Alumno (Opcional)</label>
            <input
              type="tel"
              id="phone"
              {...register('phone', {
                onChange: (e) => {
                  handlePhoneInput(e);
                },
                setValueAs: (value) => cleanPhoneNumber(value)
              })}
              placeholder="+51 900 000 000"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Datos del Padre/Tutor {isMinor() && <span style={{color: '#e74c3c'}}>*</span>}</h4>
        {isMinor() && (
          <p style={{color: '#e67e22', fontSize: '13px', marginBottom: '15px', fontStyle: 'italic'}}>
            ⚠️ Al ser menor de 18 años, los datos del padre/tutor son obligatorios
          </p>
        )}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="parent_name">
              Nombre del Padre/Tutor {isMinor() && <span style={{color: '#e74c3c'}}>*</span>}
            </label>
            <input
              type="text"
              id="parent_name"
              {...register('parent_name', {
                required: isMinor() ? 'El nombre del padre/tutor es obligatorio para menores de 18 años' : false
              })}
              className={errors.parent_name ? 'input-error' : ''}
              placeholder="Nombre completo del padre, madre o tutor"
            />
            {errors.parent_name && <span className="error-message">{errors.parent_name.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="parent_relationship">
              Relación {isMinor() && <span style={{color: '#e74c3c'}}>*</span>}
            </label>
            <select 
              id="parent_relationship" 
              {...register('parent_relationship', {
                required: isMinor() ? 'La relación es obligatoria para menores de 18 años' : false
              })}
              className={errors.parent_relationship ? 'input-error' : ''}
            >
              <option value="">Seleccionar...</option>
              <option value="Padre">Padre</option>
              <option value="Madre">Madre</option>
              <option value="Tutor">Tutor</option>
              <option value="Otro">Otro</option>
            </select>
            {errors.parent_relationship && <span className="error-message">{errors.parent_relationship.message}</span>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="parent_phone">Teléfono del Padre/Tutor</label>
            <input
              type="tel"
              id="parent_phone"
              {...register('parent_phone', {
                onChange: (e) => {
                  handlePhoneInput(e);
                },
                setValueAs: (value) => cleanPhoneNumber(value)
              })}
              placeholder="+51 900 000 000"
            />
          </div>
          <div className="form-group">
            <label htmlFor="parent_email">Email del Padre/Tutor</label>
            <input
              type="email"
              id="parent_email"
              {...register('parent_email')}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Dirección</h4>
        <div className="form-group">
          <label htmlFor="nationality_code">Nacionalidad</label>
          <input type="hidden" {...register('nationality_code')} />
          <NationalitySelect
            id="nationality_code"
            value={watch('nationality_code')}
            onChange={(code) => setValue('nationality_code', code, { shouldDirty: true })}
          />
        </div>
        <div className="form-group">
          <label htmlFor="address">Dirección</label>
          <input
            type="text"
            id="address"
            {...register('address')}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="country">País</label>
            <input type="hidden" {...register('country')} />
            <NationalitySelect
              id="country"
              value={watch('country')}
              allowEmpty
              emptyLabel="Seleccionar país..."
              options={getCountryOptions().map((opt) => ({
                code: opt.code,
                name: opt.label,
                value: opt.value,
              }))}
              onChange={(country) => {
                setValue('country', country, { shouldDirty: true });
                setValue('city', '');
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="city">Ciudad</label>
            <select
              id="city"
              {...register('city')}
              disabled={!selectedCountry}
            >
              <option value="">{selectedCountry ? 'Seleccionar ciudad...' : 'Seleccione un país primero'}</option>
              {getCitiesByCountry(selectedCountry).map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Información Adicional</h4>
        <div className="form-group">
          <label htmlFor="medical_notes">Notas Médicas</label>
          <textarea
            id="medical_notes"
            {...register('medical_notes')}
            rows="3"
            placeholder="Alergias, medicamentos, condiciones especiales..."
          />
        </div>
        <div className="form-group">
          <label htmlFor="uniform_size">Talla de Uniforme</label>
          <select id="uniform_size" {...register('uniform_size')}>
            <option value="">Seleccionar...</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
          </select>
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : initialData ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
    </>
  );
};

export default StudentForm;

